import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId && token) {
      pollPaymentStatus();
    } else if (!sessionId) {
      setStatus('error');
    }
  }, [sessionId, token]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await axios.get(
        `${API}/payments/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPaymentInfo(res.data);

      if (res.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (res.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 flex items-center justify-center bg-gradient-to-b from-muted/50 to-background">
      <div className="w-full max-w-md px-4">
        <Card className="border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            {status === 'loading' && (
              <>
                <Loader2 className="h-16 w-16 text-secondary animate-spin mx-auto mb-6" />
                <h2 className="font-heading text-2xl font-semibold text-primary mb-2">
                  Processing Payment...
                </h2>
                <p className="text-muted-foreground">
                  Please wait while we confirm your payment.
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-6" />
                <h2 className="font-heading text-2xl font-semibold text-primary mb-2">
                  Payment Successful!
                </h2>
                <p className="text-muted-foreground mb-2">
                  Thank you for your payment of ${((paymentInfo?.amount_total || 0) / 100).toLocaleString()}.
                </p>
                <p className="text-sm text-muted-foreground mb-6">
                  You will receive a confirmation email shortly.
                </p>
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={() => navigate('/dashboard')}
                    className="bg-secondary hover:bg-secondary/90"
                    data-testid="go-to-dashboard-btn"
                  >
                    Go to Dashboard
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/trips')}
                  >
                    Explore More Trips
                  </Button>
                </div>
              </>
            )}

            {status === 'timeout' && (
              <>
                <Loader2 className="h-16 w-16 text-amber-500 mx-auto mb-6" />
                <h2 className="font-heading text-2xl font-semibold text-primary mb-2">
                  Verification Pending
                </h2>
                <p className="text-muted-foreground mb-6">
                  We're still verifying your payment. Please check your email for confirmation.
                </p>
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-secondary hover:bg-secondary/90"
                >
                  Go to Dashboard
                </Button>
              </>
            )}

            {(status === 'error' || status === 'expired') && (
              <>
                <XCircle className="h-16 w-16 text-destructive mx-auto mb-6" />
                <h2 className="font-heading text-2xl font-semibold text-primary mb-2">
                  {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {status === 'expired' 
                    ? 'Your payment session has expired. Please try again.'
                    : 'We couldn\'t process your payment. Please try again or contact support.'}
                </p>
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={() => navigate('/dashboard')}
                    className="bg-secondary hover:bg-secondary/90"
                  >
                    Go to Dashboard
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/trips')}
                  >
                    Back to Trips
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
